import { type RouterOutput } from '@/config/trpc'
import { type PlatformType } from '@backend/lib/db/schema/platform'
import { type Event } from './types'

type ScheduledPost = RouterOutput['scheduledPost']['list'][number]
type TrackedPost = RouterOutput['trackedPost']['list'][number]

/**
 * Get platform color based on platform type
 */
export function getPlatformColor(platform: PlatformType): string {
  switch (platform) {
    case 'instagram':
      return '#E1306C'
    case 'linkedin':
      return '#0A66C2'
    case 'tiktok':
      return '#000000'
    case 'youtube':
      return '#FF0000'
    case 'facebook':
      return '#1877F2'
    default:
      return '#3F2AD8'
  }
}

/**
 * Extract first 5 words from text for event title
 */
function getFirstFiveWords(text: string): string {
  if (!text) return 'Untitled Post'
  const words = text.trim().split(/\s+/)
  return words.slice(0, 5).join(' ') || 'Untitled Post'
}

/**
 * Extract title from post content based on platform
 * Uses first 5 words of caption for event title
 */
function extractTitle(post: ScheduledPost): string {
  const content = post.postContent

  if (post.platform === 'youtube') {
    const youtubeContent = content as { title?: string; description?: string }
    const text = youtubeContent.title || youtubeContent.description || ''
    return getFirstFiveWords(text)
  } else {
    const captionContent = content as { caption?: string }
    const caption = captionContent.caption || ''
    return getFirstFiveWords(caption)
  }
}

/**
 * Format time from Date to HH:MM string
 */
function formatTime(date: Date): string {
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  return `${hours}:${minutes}`
}

/**
 * Convert ScheduledPost to Event
 *
 * The backend returns scheduledAt as an ISO string (UTC).
 * When we create a Date object from it, JavaScript automatically
 * converts it to the user's local timezone, which is correct.
 */
export function scheduledPostToEvent(post: ScheduledPost): Event {
  // post.scheduledAt is an ISO string from the backend (UTC)
  // new Date() automatically converts it to local timezone
  const scheduledDate = new Date(post.scheduledAt)

  return {
    id: post.id.toString(),
    title: extractTitle(post),
    date: scheduledDate, // Date in user's local timezone
    time: formatTime(scheduledDate), // Time in user's local timezone
    platform: post.platform,
    platformColor: getPlatformColor(post.platform),
    status: post.status,
    source: 'scheduled' as const,
    postId: post.id,
    postContent: post.postContent, // Include postContent for image display
    link: post.link || null, // Include link to published post
  }
}

/**
 * Convert TrackedPost to Event
 *
 * postContent is populated for source='platform' rows (published via Cassi) and
 * null for source='import'. The modal uses its presence to choose between the
 * rich platform preview and the simple media+title fallback.
 */
export function trackedPostToEvent(post: TrackedPost): Event {
  const publishedDate = new Date(post.publishedAt)

  return {
    id: `tp-${post.id}`,
    title: getFirstFiveWords(post.caption || ''),
    date: publishedDate,
    time: formatTime(publishedDate),
    platform: post.platform as PlatformType,
    platformColor: getPlatformColor(post.platform as PlatformType),
    status: post.source === 'import' ? 'imported' : 'published',
    source: 'tracked' as const,
    postId: post.scheduledPostId ?? undefined,
    trackedPostId: post.id,
    postContent: post.postContent ?? undefined,
    link: post.permalink,
    mediaUrl: post.mediaS3Url ?? null,
    caption: post.caption,
  }
}

/**
 * Convert Event to scheduled post update payload
 * Note: This only handles date/time updates, not content changes
 *
 * The date and time are already in the user's local timezone.
 * When sent to the backend, the Date object will be serialized to ISO string (UTC),
 * which is correct - the backend stores UTC timestamps.
 */
export function eventToScheduledPostUpdate(event: Event): {
  id: number
  scheduledAt: Date
} {
  const [hours, minutes] = event.time.split(':').map(Number)
  // event.date is already a Date object in local timezone
  const newDate = new Date(event.date)
  // setHours works in local timezone, which is what we want
  newDate.setHours(hours ?? 0, minutes ?? 0, 0, 0)

  return {
    id: event.postId || parseInt(event.id),
    scheduledAt: newDate, // Will be serialized to UTC ISO string when sent to backend
  }
}
